import * as React from 'react'
import { useEffect, useMemo } from 'react'

import { FilterTypeCheckboxProps } from '../types'
import { areArraysEqual } from '@/utils/areArraysEqual'
import { Filter } from '../Filter'
import { CheckboxFilterDropdownContent } from './CheckboxFilterDropdownContent'
import { useSearch } from '../../../../hooks'
import { useTranslation } from 'react-i18next'
import { useFilterCallbacks } from '../useFilterCallbacks'
import { highlightMatchingText } from '../../../../utils/highlightMatchingText'
import { Option } from '../../Form/types'
import { WidgetSpinner } from '../../Spinner/Spinner'

export const CheckboxFilter: React.FC<FilterTypeCheckboxProps> = (props) => {
    const {
        defaultValue,
        value: initialValue,
        searchConfig,
        onChange,
        useSearchParams,
        renderRichLabel,
        required,
        disabled,
        disabledLabel,
        notAvailable,
        isLoading,
    } = props

    const { t } = useTranslation('translations')
    const disabledValueLabel = notAvailable ? t(`common.n/a`) : undefined

    const {
        currentValue,
        setCurrentValue,
        onValueSave,
        onValueReset,
        resetState,
    } = useFilterCallbacks<string[]>(initialValue, defaultValue, onChange, {
        compareValues: areArraysEqual,
        onValueSaveCb: () => {
            setSearchValue('')
        },
        onValueResetCb: () => {
            setSearchValue('')
        },
        onResetStateCb: () => {
            setSearchValue('')
        },
    })

    const {
        searchValue,
        setSearchValue,
        options: displayedOptions,
    } = useSearch(
        useSearchParams ||
            (() => ({
                options: props.config.options,
            }))
    )

    const valueLabel = useMemo(() => {
        if (disabled && disabledValueLabel) {
            return disabledValueLabel
        }

        if (isLoading) {
            return <WidgetSpinner />
        }

        if (props.valueLabel) {
            return props.valueLabel
        }

        const optionsLength = props.config.options?.length ?? 0
        const selectedOptionsLength = initialValue.length

        if (
            selectedOptionsLength === 0 ||
            selectedOptionsLength === optionsLength
        ) {
            return t('filters.common.all')
        }

        if (selectedOptionsLength > 1) {
            return t('filters.common.multipleSelections', {
                selectedNumber: selectedOptionsLength,
            })
        }

        const selectedOption = props.config.options?.find((opt) =>
            initialValue.find((v) => v === opt.value)
        )

        if (selectedOption && typeof selectedOption.label === 'string') {
            return selectedOption.label
        }

        if (initialValue[0] && typeof initialValue[0] === 'string') {
            return initialValue[0]
        }

        return ''
    }, [
        disabled,
        disabledValueLabel,
        initialValue,
        isLoading,
        props.config.options,
        props.valueLabel,
        t,
    ])

    useEffect(() => {
        const validSelectedOptions: string[] = []
        const options = props.config.options

        if (options) {
            initialValue.forEach((selectedOption) => {
                const isValidOption = options.find(
                    (opt) => selectedOption === opt.value
                )
                if (isValidOption) {
                    validSelectedOptions.push(selectedOption)
                }
            })
            if (validSelectedOptions.length < initialValue.length) {
                onChange(validSelectedOptions)
            }
        }
    }, [initialValue, onChange, props.config.options, required])

    const searchFilterConfig = searchConfig
        ? {
              ...searchConfig,
              value: searchValue,
              onChange: setSearchValue,
          }
        : undefined

    const renderLabel = useMemo(() => {
        if (props.renderLabel) return props.renderLabel

        if (renderRichLabel) {
            const richLabelFn = (option: Option): React.ReactNode => {
                return renderRichLabel(option, (input) =>
                    highlightMatchingText({
                        text: input,
                        matchText: searchValue,
                    })
                )
            }
            richLabelFn.displayName = 'renderRichLabel'
            return richLabelFn
        }
    }, [props.renderLabel, renderRichLabel, searchValue])

    return (
        <Filter
            {...props}
            label={disabled && disabledLabel ? disabledLabel : props.label}
            valueLabel={disabledValueLabel ?? valueLabel}
            onValueSave={onValueSave}
            onValueReset={onValueReset}
            resetState={resetState}
            showReset
            searchConfig={searchFilterConfig}
            options={displayedOptions}
            containerRef={props.containerRef}
        >
            <CheckboxFilterDropdownContent
                disabled={props.disabled}
                name={props.name}
                options={displayedOptions}
                value={currentValue}
                onChange={setCurrentValue}
                renderLabel={renderLabel}
            />
        </Filter>
    )
}
